import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Mail, Smartphone } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/* Toggle between "Mobile OTP" and "Email & password" on Login / Signup. */
export function AuthMethodToggle({ method, onChange, prefix }) {
    const btn = (key, Icon, label) => (
        <button
            type="button"
            onClick={() => onChange(key)}
            data-testid={`${prefix}-method-${key}`}
            aria-pressed={method === key}
            className={`flex-1 h-10 rounded-xl text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
                method === key ? "bg-[#1B5E20] text-white shadow-sm" : "text-[#4A5D4E] hover:bg-[#F1F7EE]"
            }`}
        >
            <Icon className="h-4 w-4" /> {label}
        </button>
    );
    return (
        <div className="mb-4 p-1 rounded-2xl bg-[#F3F5F1] border border-[#E3E6E0] flex gap-1" data-testid={`${prefix}-method-toggle`}>
            {btn("phone", Smartphone, "Mobile OTP")}
            {btn("email", Mail, "Email & password")}
        </div>
    );
}

export function EmailPasswordForm({ mode, onSuccess, prefix, inviteCode }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [remember, setRemember] = useState(false);
    const [busy, setBusy] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [useOtp, setUseOtp] = useState(false); // login: passwordless email OTP
    const [cooldown, setCooldown] = useState(0);
    const isSignup = mode === "signup";
    const needsOtp = isSignup || useOtp;

    const sendOtp = async () => {
        if (!/^\S+@\S+\.\S+$/.test(email.trim())) { toast.error("Please enter a valid email address."); return; }
        setBusy(true);
        try {
            await api.post("/auth/email-otp/send", { email: email.trim() });
            setOtpSent(true);
            setCooldown(45);
            const t = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; }), 1000);
            toast.success("Verification code sent to your email (check spam too).");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (isSignup && name.trim().length < 2) { toast.error("Please enter your full name."); return; }
        if (!/^\S+@\S+\.\S+$/.test(email.trim())) { toast.error("Please enter a valid email address."); return; }
        if (isSignup && password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
        if (needsOtp && !otpSent) { await sendOtp(); return; }
        if (needsOtp && otp.trim().length < 4) { toast.error("Enter the 6-digit code sent to your email."); return; }
        setBusy(true);
        try {
            const url = isSignup ? "/auth/register-email" : useOtp ? "/auth/email-otp/verify" : "/auth/login-email";
            const body = isSignup
                ? { name: name.trim(), email: email.trim(), otp: otp.trim(), password, invite_code: inviteCode || null, remember_me: remember }
                : useOtp
                    ? { email: email.trim(), otp: otp.trim(), remember_me: remember }
                    : { email: email.trim(), password, remember_me: remember };
            const { data } = await api.post(url, body);
            onSuccess(data.user);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const cls = "h-12 rounded-xl border-[#D9D4C7] text-[15px]";
    return (
        <form onSubmit={submit} noValidate className="space-y-3" data-testid={`${prefix}-email-form`}>
            {isSignup && (
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" autoComplete="name" className={cls} data-testid={`${prefix}-email-name`} />
            )}
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" autoComplete="email" className={cls} data-testid={`${prefix}-email-input`} />
            {!useOtp && (
            <div className="relative">
                <Input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={isSignup ? "Create password (min 8 characters)" : "Password"} autoComplete={isSignup ? "new-password" : "current-password"} className={`${cls} pr-11`} data-testid={`${prefix}-email-password`} />
                <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"} data-testid={`${prefix}-email-toggle-password`} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7A6E]">
                    {show ? <EyeOff className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />}
                </button>
            </div>
            )}
            {needsOtp && otpSent && (
                <div className="rounded-xl border border-[#C8E6C9] bg-[#F1F7EE] p-3" data-testid={`${prefix}-email-otp-box`}>
                    <label className="text-[12.5px] font-semibold text-[#1B5E20]">Enter the 6-digit code sent to {email.trim()}</label>
                    <Input inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="••••••" className="mt-1.5 h-12 rounded-xl border-[#C8E6C9] text-[20px] tracking-[0.4em] text-center font-bold" data-testid={`${prefix}-email-otp`} />
                    <button type="button" disabled={cooldown > 0 || busy} onClick={sendOtp} data-testid={`${prefix}-email-otp-resend`} className="mt-1.5 text-[12.5px] font-semibold text-[#1B5E20] disabled:text-[#9CA3AF]">
                        {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
                    </button>
                </div>
            )}
            <label className="flex items-center gap-2 text-[13px] text-[#4A5D4E] cursor-pointer">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid={`${prefix}-email-remember`} className="h-4 w-4 accent-[#1B5E20]" /> Keep me logged in for 30 days
            </label>
            <Button type="submit" disabled={busy} data-testid={`${prefix}-email-submit`} className="w-full h-12 rounded-xl bg-[#1B5E20] hover:bg-[#0F3311] text-white text-[15px] font-semibold">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : needsOtp && !otpSent ? "Send verification code to email" : isSignup ? "Verify & create account" : "Log in"}
            </Button>
            {!isSignup && (
                <button type="button" onClick={() => { setUseOtp((v) => !v); setOtpSent(false); setOtp(""); }} data-testid={`${prefix}-email-otp-toggle`} className="w-full text-center text-[13px] font-semibold text-[#6D28D9] hover:underline">
                    {useOtp ? "Use password instead" : "Log in with a code sent to my email (no password)"}
                </button>
            )}
        </form>
    );
}
